import { useEffect, useRef, useState } from 'react';
import {
  Box,
  HStack,
  VStack,
  Text,
  SimpleGrid,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
  chakra,
} from '@chakra-ui/react';
import { Info } from 'lucide-react';
import { motion } from 'framer-motion';
import { haptics } from '@/utils/haptics';
import { COMPATIBILITY_LEVELS, NOT_SURE_EMOJI_CHAR, RATING_EMOJIS } from '@/utils/restaurantUtils';
import { mapPinKeyCopy } from '@/copy/mapPinKey';
import { getFaceMetrics, greyscaleImage } from './pinTint';
import { pinUrl } from './pinAssets';

const MotionButton = motion.create(chakra.button);

// Old key order: best to worst (Highest, High, Medium, Some, Low, No). The
// level word comes from RATING_EMOJIS; the pin asset name is derived exactly
// as the real pin logic does (getEmojiKeyFromLevel + generatePinKey in
// restaurantUtils.tsx: `${COMPATIBILITY_LEVELS[i].key}CompatibilityPin`), so
// there is no hand-kept pairing. A level with no matching key is dropped
// rather than rendered as an empty box.
const personalLevels = RATING_EMOJIS.flatMap((level, index) => {
  const key = COMPATIBILITY_LEVELS[index]?.key;
  return key ? [{ label: level.label, src: pinUrl(`${key}CompatibilityPin`) }] : [];
}).reverse();

// 32 x 48 keeps the pin PNGs' 400 x 600 aspect ratio.
const PIN_W_PX = 32;
const PIN_H_PX = 48;
const FACE = getFaceMetrics(PIN_W_PX, PIN_H_PX);

/** One real pin image. Decorative: the adjacent text carries the meaning. */
const PinImage = ({
  src,
  withNotSureFace = false,
  greyFallback = false,
  onError,
}: {
  src?: string;
  withNotSureFace?: boolean;
  greyFallback?: boolean;
  onError?: () => void;
}) => (
  <Box position="relative" w={`${PIN_W_PX}px`} h={`${PIN_H_PX}px`} flexShrink={0}>
    {src && (
      <Box
        as="img"
        src={src}
        alt=""
        w="100%"
        h="100%"
        objectFit="contain"
        draggable={false}
        // Brings the yellow body down to the map's grey 166/255 (~0.65).
        // CSS grayscale() weights channels with Rec.709 luma; with those
        // weights the factor is 0.69 (the Rec.601 estimate, 0.65 / 0.92, would
        // give ~0.71, which is not what this filter computes).
        filter={greyFallback ? 'grayscale(1) brightness(0.69)' : undefined}
        onError={onError}
      />
    )}
    {withNotSureFace && (
      // The no-data base art has no face; RestaurantMapView composites this
      // character onto it at runtime. Size and position come from the same
      // measured ratios as the map (getFaceMetrics, pinTint.ts), so the key
      // matches the map and the level faces on the personal pins.
      <Text
        as="span"
        aria-hidden="true"
        position="absolute"
        left={`${FACE.centerX}px`}
        top={`${FACE.centerY}px`}
        transform="translate(-50%, -50%)"
        fontSize={`${FACE.fontSize}px`}
        lineHeight="1"
      >
        {NOT_SURE_EMOJI_CHAR}
      </Text>
    )}
  </Box>
);

/**
 * The grey "Unsure" pin data URL: the yellow no-data asset recolored with the
 * same pixel conversion the map uses. Built once per page load and shared by
 * every open of the dialog. Resolves `undefined` when canvas is unavailable
 * or anything fails, so callers can fall back to a CSS-grey base image.
 */
let greyUnsurePinUrl: Promise<string | undefined> | undefined;
const loadGreyUnsurePinUrl = (): Promise<string | undefined> => {
  greyUnsurePinUrl ??= new Promise((resolve) => {
    const baseUrl = pinUrl('00_noDataPin');
    if (!baseUrl) {
      resolve(undefined);
      return;
    }
    const img = new window.Image();
    img.onload = () => {
      try {
        resolve(greyscaleImage(img)?.toDataURL('image/png'));
      } catch {
        resolve(undefined);
      }
    };
    img.onerror = () => resolve(undefined);
    img.src = baseUrl;
  });
  return greyUnsurePinUrl;
};

/**
 * The grey "Unsure" pin plus the 🤔 overlay. Mounts only while the dialog is
 * open. If the recolored image is unavailable (no canvas) or fails to load,
 * shows the unmodified asset with a CSS grayscale filter, so the row is never
 * empty and never yellow.
 */
const UnsurePinImage = () => {
  const baseUrl = pinUrl('00_noDataPin');
  const [greyUrl, setGreyUrl] = useState<string | undefined>();

  useEffect(() => {
    let cancelled = false;
    loadGreyUnsurePinUrl().then((url) => {
      if (!cancelled) setGreyUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <PinImage
      src={greyUrl ?? baseUrl}
      withNotSureFace
      greyFallback={!greyUrl}
      onError={() => setGreyUrl(undefined)}
    />
  );
};

const PinRow = ({
  pin,
  title,
  description,
  testId,
}: {
  pin?: React.ReactNode;
  testId?: string;
  title: string;
  description: string;
}) => (
  <HStack spacing={3} align="center" data-testid={testId}>
    {pin}
    <VStack align="start" spacing={0} flex={1}>
      <Text fontSize="xs" fontWeight="semibold" color="var(--card-text)">
        {title}
      </Text>
      <Text fontSize="xs" color="var(--card-text-2)" lineHeight="1.4">
        {description}
      </Text>
    </VStack>
  </HStack>
);

// Derived directly from getRatingIconPath/generatePinKey (restaurantUtils.tsx)
// and the pin compositing in RestaurantMapView.tsx — keep the groups below in
// sync with that logic, not the other way around. Since 2026-10-04 (CD) the
// map has ONE rule for "no personal TasteMatch": a grey pin with the 🤔 face
// ("Unsure TasteMatch"), whether or not a public/Google rating exists. The public rating
// no longer colors or faces any pin, and the yellow no-data pin and the
// public-level-face grey pins are no longer selected. Pin body COLOR encodes
// the data *source* (and, for personal ratings, the level); pin FACE encodes
// the *compatibility level* (or the 🤔 "not sure" face):
//  - Personal rating exists   -> pin body on a red->green scale for your own
//    (source: personal)          TasteMatch level (worst=red, best=green);
//                                 face = the matching level emoji. Real
//                                 designed assets, shown as a strip of the six
//                                 level pins captioned with the RATING_EMOJIS
//                                 level words.
//  - No personal rating       -> pin body is flat GREY with the 🤔 face
//    (public rating or not)      ("Unsure TasteMatch"). No grey+🤔 asset exists: the map
//                                 recolors the yellow 00_noDataPin base to
//                                 grey at runtime (pinTint.ts) and composites
//                                 NOT_SURE_EMOJI_CHAR; the key does the same
//                                 (see UnsurePinImage).
//  - On one of your lists     -> BLUE overlays the body above as an
//    (isBookmarked, orthogonal    ORTHOGONAL signal — it says nothing about
//    to the states above)        data confidence. Personal+listed is a fully
//                                 blue pin with the level face; Unsure+listed
//                                 is the grey/blue split (left half grey,
//                                 right half blue) with the 🤔 face. The key
//                                 shows the blue personal listed pin and
//                                 describes the split in words.

/**
 * Pin Key: a small "Pin Key" trigger on the map that opens a centered modal
 * explaining what map pin colors and the 🤔 face mean. The modal shows the
 * pin art in three groups (personal TasteMatch, unsure, listed) plus the
 * legacy key's explanatory paragraphs. A modal has no
 * "expanded" state, so the trigger carries no chevron.
 * See paire-app#1 for the policy this implements.
 */
export function MapPinLegend() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  // Returns focus to the trigger on close (iOS/WKWebView: default focus return
  // targets whatever was active before open). Set before opening, as Chakra
  // only checks that a ref object was passed, not its `.current`.
  const triggerRef = useRef<HTMLButtonElement>(null);

  const openKey = () => {
    haptics.selection();
    onOpen();
  };

  return (
    <>
      <MotionButton
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        position="absolute"
        // MainNavigationTabBar.tsx is a `position: fixed` bottom bar with no
        // explicit height (icon + label + safe-area padding, ~70-80px on most
        // devices) — bottom={4} (16px) would sit underneath it. Clear it with
        // margin to spare; still resolves to a small, sane offset on any
        // screen that doesn't render the tab bar at all.
        bottom="calc(76px + env(safe-area-inset-bottom))"
        left={4}
        zIndex={5}
        // D45b Phase 6 (FR-N9, NFR-4) glass-roster sweep: this trigger is not
        // one of the six roster surfaces (its blur predates the roster and
        // was found unconverted in D45a's Phase 5 audit, "components/map/").
        // blackAlpha.800 is already opaque enough for legibility without it.
        bg="blackAlpha.800"
        borderRadius="xl"
        px={3}
        py={2}
        borderWidth="1px"
        borderColor="whiteAlpha.200"
        onClick={openKey}
        whileTap={{ scale: 0.98 }}
        transition={{ duration: 0.1 }}
      >
        <HStack spacing={2}>
          <Info size={16} color="var(--chakra-colors-purple-300)" />
          {/* TEXT-COLOR-LITERAL: the trigger floats over the map and is always dark (blackAlpha.800) in both color modes */}
          <Text fontSize="sm" fontWeight="semibold" color="white">
            {mapPinKeyCopy.heading}
          </Text>
        </HStack>
      </MotionButton>

      {/* Closes on overlay click and Esc (Chakra defaults). scrollBehavior
          "inside" keeps the header pinned and scrolls only the body, and the
          max height subtracts the safe-area insets so short phones scroll
          instead of clipping under the notch / home indicator. */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside" finalFocusRef={triggerRef}>
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
        <ModalContent
          bg="var(--card-bg)"
          maxW="400px"
          maxH="calc(100dvh - 32px - env(safe-area-inset-top) - env(safe-area-inset-bottom))"
          mx={4}
          borderRadius="var(--r-card)"
          overflow="hidden"
        >
          <ModalHeader color="var(--card-text)" fontSize="md" pr={12} flexShrink={0}>
            {mapPinKeyCopy.heading}
          </ModalHeader>
          <ModalCloseButton color="var(--card-text-2)" _hover={{ color: 'var(--card-text)' }} />
          <ModalBody pb={5} overflowY="auto">
            <VStack align="stretch" spacing={3}>
              <VStack align="stretch" spacing={2}>
                <PinRow
                  title={mapPinKeyCopy.personal.title}
                  description={mapPinKeyCopy.personal.description}
                />
                {/* 3 columns x 2 rows keeps "Highest" at the 12px floor from crowding on narrow phones. */}
                <SimpleGrid columns={3} spacing={2} data-testid="pin-key-personal-levels">
                  {personalLevels.map((level) => (
                    <VStack key={level.label} spacing={0.5}>
                      <PinImage src={level.src} />
                      <Text fontSize="xs" color="var(--card-text-2)">
                        {level.label}
                      </Text>
                    </VStack>
                  ))}
                </SimpleGrid>
              </VStack>

              <PinRow
                testId="pin-key-unsure-row"
                pin={<UnsurePinImage />}
                title={mapPinKeyCopy.unsure.title}
                description={mapPinKeyCopy.unsure.description}
              />
              <PinRow
                pin={<PinImage src={pinUrl('04_mediumCompatibilityListPin')} />}
                title={mapPinKeyCopy.listed.title}
                description={mapPinKeyCopy.listed.description}
              />

              <Divider borderColor="var(--card-divider)" />

              <VStack align="stretch" spacing={2}>
                {mapPinKeyCopy.explanation.map((paragraph) => (
                  <Text key={paragraph} fontSize="xs" color="var(--card-text-2)" lineHeight="1.4">
                    {paragraph}
                  </Text>
                ))}
              </VStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

export default MapPinLegend;
